import * as React from 'react';

import {AlertBox, AlertBoxSeverity} from '../../../components/AlertBox';
import {AuthoringWrapper} from '../../../components/AuthoringWrapper';
import {MapTo} from '../../../infrastructure/compatibility/MapTo';
import {C} from '../../../registries/compatibilty';
import {SectionWrapper} from './SectionWrapper';
import {PremiereSectionModel} from '../../../../generated/core';
import {PremiereSection as PremiereSectionLSG} from '../../../d6/components/premiere';
import {CountdownItem} from '../items/PremiereCountdownItem';
import {RevealItem} from '../items/PremiereRevealItem';
import {ResponsiveMediaRendererConf} from '../../../components/ResponsiveMediaRenderer';
import {CountdownState} from '../../../context/tracking/TrackingService';
import {useRegistry, useTrackingService} from '../../../context';
import {ContainerItem} from '../../../infrastructure/ContainerExporter';
import {ButtonElement} from '../elements/buttonElement/ButtonElement';
import {SerializedStateManagerV1} from '@feature-hub/serialized-state-manager';
import {parseHeadingRichtextToString} from '../../../utils/parseHeadingRichtextToString';

export type TransitionState = 'before' | 'transition' | 'after';

export const premiereResponsiveMediaConfig: ResponsiveMediaRendererConf[] = [
    {
        mediaQuery: '(max-aspect-ratio: 1/1)',
        sizes: '100vw',
        aspectRatio: 'r_4_5',
        portray: true
    },
    {
        mediaQuery: '(min-aspect-ratio: 1/1) and (max-aspect-ratio: 4/3)',
        sizes: '100vw',
        aspectRatio: 'r_4_3'
    },
    {
        mediaQuery: '(min-aspect-ratio: 4/3)',
        sizes: '100vw',
        aspectRatio: 'r_16_9'
    }
];

export const PremiereButtonElements = (props: {
    buttons: ContainerItem[];
    countdownState: CountdownState;
}) => {
    const trackingService = useTrackingService();

    return (
        <>
            {props.buttons.map((child: ContainerItem) => {
                const trackButtonClick = (href: string, linkName: string) =>
                    trackingService.trackButtonClick(
                        child.key,
                        href,
                        linkName,
                        {countdownState: props.countdownState}
                    );
                return (
                    <ButtonElement
                        path={`buttonsParsys/${child.key}`}
                        key={child.key}
                        trackingActionOverride={trackButtonClick}
                    />
                );
            })}
        </>
    );
};

const InternalPremiereSection = (
    props: PremiereSectionModel
): React.ReactElement => {
    const {
        anchorId,
        contentId: sectionId,
        contentName,
        contentLabels,
        startOfReveal,
        tagTypeBroken,
        mboxId,
        premiereSectionLabel,
        cqItems
    } = props;

    const timeDistance = startOfReveal - Date.now();

    const beforeState = useIsStateBeforeReveal(timeDistance);

    const [transitionState, setTransitionState] =
        React.useState<TransitionState>(() =>
            beforeState ? 'before' : 'after'
        );

    /* these values are used as fallback for both the section aria-label
    and for the hidden heading in case authors do not maintain the premiereSectionLabel */
    const countdownHeading = parseHeadingRichtextToString(
        cqItems.countdownItem.cqItems.heading.richtext
    );
    const revealHeading = parseHeadingRichtextToString(
        cqItems.revealItem.cqItems.heading.richtext
    );
    const fallbackHeading =
        transitionState === 'before' ? countdownHeading : revealHeading;

    const sectionAriaLabel = premiereSectionLabel ?? fallbackHeading;

    const countdownItem = (
        <CountdownItem
            startOfReveal={startOfReveal}
            timeDistance={timeDistance}
            path="countdownItem"
        />
    );
    const revealItem = <RevealItem path="revealItem" />;

    const premiereSection = C.isInEditor() ? (
        <>
            {timeDistance < 0 ? (
                <AlertBox severity={AlertBoxSeverity.ERROR}>
                    <span>
                        Please set the Pre Order Date or one that is not in the
                        past
                    </span>
                </AlertBox>
            ) : (
                <AlertBox severity={AlertBoxSeverity.INFO}>
                    <span>
                        Countdown date set to:{' '}
                        {new Date(startOfReveal).toLocaleString()}
                    </span>
                </AlertBox>
            )}
            {countdownItem}
            {revealItem}
        </>
    ) : (
        <PremiereSectionLSG
            beforeReveal={countdownItem}
            afterReveal={revealItem}
            beforeState={beforeState}
            timeDistance={timeDistance}
            showHiddenHeadline={Boolean(premiereSectionLabel)}
            transitionState={transitionState}
            setTransitionState={setTransitionState}
        />
    );

    return (
        <AuthoringWrapper
            anchorId={anchorId}
            title="S140 Premiere Section"
            bgColor={AuthoringWrapper.BG_COLOR_SECTION}
            tagTypeBroken={tagTypeBroken}
        >
            <SectionWrapper
                mboxId={mboxId}
                anchorId={anchorId}
                sectionId={sectionId}
                contentLabels={contentLabels}
                key={sectionId}
                contentName={contentName}
                sectionAriaLabel={sectionAriaLabel}
                // NOTE: ideally if a heading is present we should use aria-labelledby instead of aria-label
            >
                {premiereSection}
            </SectionWrapper>
        </AuthoringWrapper>
    );
};
export const PremiereSection = MapTo(
    'vwa-ngw18/components/editorial/sections/premiereSection',
    InternalPremiereSection
);

/**
 *
 * @param timeDistance current time distance
 * @returns true if the component was rendered (on ssr or client) before the reveal time
 */
export function useIsStateBeforeReveal(timeDistance: number) {
    const s: SerializedStateManagerV1 = useRegistry().getSingleton(
        'SerializedStateManager'
    );

    s.register(() => {
        return String(timeDistance > 0);
    });

    return s.getSerializedState() === 'true' || timeDistance > 0;
}
